import React, { useEffect, useRef } from 'react';

type UseVisiblePdfPagesParams = {
  numPages: number | null;
  pageRefsRef: React.MutableRefObject<(HTMLDivElement | null)[]>;
  currentPageFromScrollRef: React.MutableRefObject<number>;
  setMountedPages: React.Dispatch<React.SetStateAction<Set<number>>>;
  onPageChangeFromScroll: (page: number) => void;
  maxMountedPages: number;
  prefetchMarginRatio?: number;
  focusedPage?: number;
  /** Re-sync when layout changes (e.g. placeholder heights computed). */
  layoutRevision?: number;
};

function trimToMax(pages: Set<number>, anchor: number, max: number): Set<number> {
  if (pages.size <= max) return pages;
  const ranked = [...pages].sort((a, b) => Math.abs(a - anchor) - Math.abs(b - anchor) || a - b);
  return new Set(ranked.slice(0, max));
}

function addFocusedNeighbors(pages: Set<number>, focusedPage: number | undefined, numPages: number) {
  if (!focusedPage) return pages;
  const neighbors = [focusedPage - 1, focusedPage, focusedPage + 1].filter(page => page >= 1 && page <= numPages);
  return new Set([...pages, ...neighbors]);
}

export function useVisiblePdfPages({
  numPages,
  pageRefsRef,
  currentPageFromScrollRef,
  setMountedPages,
  onPageChangeFromScroll,
  maxMountedPages,
  prefetchMarginRatio = 0.28,
  focusedPage,
  layoutRevision = 0,
}: UseVisiblePdfPagesParams) {
  const onPageChangeRef = useRef(onPageChangeFromScroll);
  onPageChangeRef.current = onPageChangeFromScroll;

  useEffect(() => {
    if (numPages == null || numPages < 1 || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      entries => {
        setMountedPages(prev => {
          const next = new Set(prev);
          for (const entry of entries) {
            const pageNum = Number((entry.target as HTMLElement).dataset.pageNumber);
            if (pageNum < 1 || Number.isNaN(pageNum)) continue;
            if (entry.isIntersecting) next.add(pageNum);
            else next.delete(pageNum);
          }
          const withFocused = addFocusedNeighbors(next, focusedPage, numPages);
          const anchor = currentPageFromScrollRef.current;
          return trimToMax(withFocused, anchor, maxMountedPages);
        });

        let bestPage = 0;
        let bestRatio = 0;
        for (const entry of entries) {
          const pageNum = Number((entry.target as HTMLElement).dataset.pageNumber);
          if (pageNum >= 1 && !Number.isNaN(pageNum) && entry.intersectionRatio >= 0.5) {
            if (entry.intersectionRatio > bestRatio) {
              bestRatio = entry.intersectionRatio;
              bestPage = pageNum;
            }
          }
        }
        if (bestPage >= 1 && bestPage !== currentPageFromScrollRef.current) {
          currentPageFromScrollRef.current = bestPage;
          onPageChangeRef.current(bestPage);
        }
      },
      {
        // Viewport root works whether ScrollArea or a parent scrolls (common on mobile).
        root: null,
        rootMargin: `${Math.round(prefetchMarginRatio * 100)}% 0px`,
        threshold: [0, 0.5, 1],
      }
    );

    pageRefsRef.current
      .slice(0, numPages)
      .filter((el): el is HTMLDivElement => el != null)
      .forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, [
    numPages,
    pageRefsRef,
    currentPageFromScrollRef,
    setMountedPages,
    maxMountedPages,
    prefetchMarginRatio,
    focusedPage,
    layoutRevision,
  ]);
}
